<template>
  <div class="project-load-revit">
    <div style="padding: 20px;">
      <div style="margin-bottom: 20px;">
        <h5 style="margin: 0 0 16px 0; font-size: 18px; font-weight: 600;">
          Подгрузить Revit проект
        </h5>
        <p style="color: #666; margin: 0 0 24px 0;">
          Выберите подъезд и BIM проект для загрузки данных
        </p>
      </div>

      <q-linear-progress v-if="isLoading" indeterminate />

      <!-- Прогресс импорта -->
      <div v-if="importProgress.active" class="import-progress-section" style="margin-bottom: 20px;">
        <q-card flat bordered>
          <q-card-section>
            <!-- Сообщение о блокировке (подложки не загружены и т.п.) -->
            <div
              v-if="importProgress.errorMessage"
              class="tw-rounded tw-p-3 tw-mb-3"
              style="background: #ffebee; color: #c62828; border: 1px solid #ef9a9a;"
            >
              <div style="font-weight: 500; margin-bottom: 8px;">Импорт невозможен</div>
              <div style="font-size: 13px; line-height: 1.5; white-space: pre-wrap;">{{ importProgress.errorMessage }}</div>
              <q-btn
                flat
                dense
                label="Понятно"
                color="primary"
                class="tw-mt-2"
                @click="clearImportError"
              />
            </div>
            <div v-else style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <q-spinner v-if="importProgress.isLoading" color="primary" size="24px" />
              <q-icon v-else name="check_circle" color="positive" size="24px" />
              <div style="font-weight: 500; font-size: 16px;">
                {{ importProgress.isLoading ? 'Импорт BIM проекта' : 'Импорт завершен' }}
              </div>
            </div>
            
            <!-- Список сообщений (только при отсутствии ошибки блокировки) -->
            <div 
              v-if="!importProgress.errorMessage && importProgress.messages.length > 0"
              ref="messagesContainer"
              style="max-height: 200px; overflow-y: auto; background: #f5f5f5; border-radius: 4px; padding: 8px;"
            >
              <div 
                v-for="(msg, index) in importProgress.messages" 
                :key="index"
                style="padding: 4px 0; font-size: 13px; color: #666; line-height: 1.4;"
              >
                <span style="color: #999; margin-right: 8px;">•</span>{{ msg }}
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>

      <div v-if="!isLoading && !importProgress.active">
        <!-- Выбор подъезда -->
        <div class="form-section">
          <label class="form-label">
            Подъезд <span style="color: red;">*</span>
          </label>
          <q-select
            v-model="selectedEntrance"
            :options="entrances"
            option-label="entrance_name"
            option-value="project_entrance_id"
            emit-value
            map-options
            outlined
            dense
            placeholder="Выберите подъезд"
            :clearable="false"
          />
        </div>

        <!-- Выбор BIM проекта -->
        <div class="form-section">
          <label class="form-label">
            BIM проект <span style="color: red;">*</span>
          </label>
          <div v-if="isLoadingBimProjects" style="padding: 20px; text-align: center;">
            <q-spinner color="primary" size="30px" />
          </div>
          <div v-else>
            <q-input
              v-model="bimProjectSearchQuery"
              dense
              outlined
              placeholder="Поиск BIM проекта..."
              clearable
              class="search-input"
              style="margin-bottom: 12px;"
            >
              <template v-slot:prepend>
                <q-icon name="search" size="16px" />
              </template>
            </q-input>
            
            <div 
              v-if="filteredBimProjects.length > 0" 
              class="bim-projects-list"
              style="max-height: 300px; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 4px;"
            >
              <div
                v-for="project in filteredBimProjects"
                :key="project.bim_project_id"
                @click="selectBimProject(project)"
                :class="['bim-project-item', { 'selected': selectedBimProjectId === project.bim_project_id }]"
                style="padding: 12px; border-bottom: 1px solid #f0f0f0; cursor: pointer; transition: background 0.2s;"
              >
                <div style="font-weight: 500; margin-bottom: 4px;">
                  {{ project.project_name }}
                </div>
                <div v-if="project.comments" style="font-size: 12px; color: #666;">
                  {{ project.comments }}
                </div>
              </div>
            </div>
            <div v-else style="padding: 20px; text-align: center; color: #999; background: #f5f5f5; border-radius: 4px;">
              {{ bimProjectSearchQuery ? 'Ничего не найдено' : 'Нет доступных BIM проектов' }}
            </div>
          </div>
        </div>

        <!-- Кнопки -->
        <div style="display: flex; gap: 12px; margin-top: 24px; justify-content: flex-end;">
          <q-btn
            outline
            label="Отмена"
            color="grey-7"
            @click="$emit('close')"
            :disable="isSubmitting"
          />
          <q-btn
            unelevated
            label="Подгрузить"
            color="primary"
            @click="handleSubmit"
            :loading="isSubmitting"
            :disable="!selectedEntrance || !selectedBimProjectId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBimProjects } from '@/views/BimProjects/services';
import { getEntrancesByProject } from '@/views/ProjectsEntry/services';
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import { showInfoToast } from '@/services';
import instance from '@/services/api';

export default {
  name: 'ProjectLoadRevit',
  props: {
    projectId: {
      type: Number,
      required: true
    },
    residentId: {
      type: Number,
      required: false // Больше не используется, но оставлен для обратной совместимости
    },
    projectTypeId: {
      type: Number,
      required: false
    },
    initialProjectEntranceId: {
      type: Number,
      default: null
    },
    initialBimProjectId: {
      type: Number,
      default: null
    },
    initialOperationKey: {
      type: String,
      default: null
    },
    importInProgress: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      isLoadingBimProjects: false,
      isSubmitting: false,
      entrances: [],
      selectedEntrance: null,
      bimProjects: [],
      selectedBimProjectId: null,
      bimProjectSearchQuery: '',
      importProgress: {
        active: false,
        messages: [],
        isLoading: false,
        errorMessage: null
      },
      pollingInterval: null,
      /** Флаг, чтобы при нескольких ответах polling с completed не показывать уведомление и не эмитить несколько раз */
      completedHandledForCurrentImport: false,
      /** Этажи, которые были только в Ревите до загрузки (only_in_structure) — после успешного импорта показываем, что они добавлены */
      lastMismatchWarning: null
    };
  },
  computed: {
    filteredBimProjects() {
      if (!this.bimProjectSearchQuery) {
        return this.bimProjects;
      }
      const query = this.bimProjectSearchQuery.toLowerCase().trim();
      return this.bimProjects.filter(project => {
        return (
          project.project_name?.toLowerCase().includes(query) ||
          (project.comments && project.comments.toLowerCase().includes(query)) ||
          (project.bim_project_id != null && String(project.bim_project_id).toLowerCase().includes(query))
        );
      });
    }
  },
  methods: {
    async loadData() {
      this.isLoading = true;

      const entrancesData = await getEntrancesByProject(this.projectId);
      if (entrancesData) {
        this.entrances = entrancesData;
      }

      if (this.initialProjectEntranceId && this.entrances.some(e => Number(e.project_entrance_id) === Number(this.initialProjectEntranceId))) {
        this.selectedEntrance = this.initialProjectEntranceId;
      }

      this.isLoading = false;

      await this.loadBimProjects();
    },

    async loadBimProjects() {
      this.isLoadingBimProjects = true;
      const data = await getBimProjects();
      this.bimProjects = data || [];
      const inList = (id) => this.bimProjects.some(p => Number(p.bim_project_id) === Number(id));
      if (this.selectedBimProjectId && !inList(this.selectedBimProjectId)) {
        this.selectedBimProjectId = null;
      }
      if (this.initialBimProjectId && inList(this.initialBimProjectId)) {
        this.selectedBimProjectId = this.initialBimProjectId;
      }
      this.isLoadingBimProjects = false;
    },
    
    selectBimProject(project) {
      this.selectedBimProjectId = project.bim_project_id;
    },
    
    async handleSubmit() {
      if (!this.selectedEntrance || !this.selectedBimProjectId) {
        return;
      }
      
      this.isSubmitting = true;
      this.importProgress.active = true;
      this.importProgress.isLoading = true;
      this.importProgress.messages = [];
      
      const body = {
        bim_project_id: this.selectedBimProjectId,
        project_entrance_id: this.selectedEntrance
      };
      
      try {
        const response = await instance().post('/project_list/project/load_revit/', body);
        const operationKey = response.data?.operation_key;
        const mismatchWarning = response.data?.mismatch_warning || null;
        this.lastMismatchWarning = mismatchWarning || null;

        if (mismatchWarning?.message) {
          this.showWarningSnackbar(mismatchWarning.message);
        }

        if (operationKey) {
          this.completedHandledForCurrentImport = false;
          this.startPolling(operationKey);
        } else {
          this.isSubmitting = false;
          this.importProgress.active = false;
          this.showSucceedSnackbar('Revit проект успешно загружен');
          this.$emit('success');
        }
      } catch (error) {
        this.isSubmitting = false;
        this.importProgress.active = true;
        this.importProgress.isLoading = false;
        this.importProgress.messages = [];
        const msg = error.response?.data?.error || error.response?.data?.detail || error.message || 'Ошибка при запуске импорта';
        this.importProgress.errorMessage = typeof msg === 'string' ? msg : 'Ошибка при запуске импорта';
        this.showErrorSnackbar(typeof msg === 'string' ? msg : 'Ошибка при запуске импорта');
      }
    },
    
    startPolling(operationKey) {
      // Polling каждые 1 секунду
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await instance().get('/project_list/project/operation_progress/', {
            params: { operation_key: operationKey }
          });
          
          const data = response.data;
          const messages = data?.messages || [];
          const status = data?.operation_status || 'running';
          const errorMessage = data?.error_message;
          
          // Обновляем список сообщений (извлекаем только текст сообщений)
          this.importProgress.messages = messages.map(m => m.message || '').filter(m => m);
          
          // Прокручиваем к последнему сообщению
          this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          });
          
          // Проверяем статус
          if (status === 'completed') {
            this.stopPolling();
            this.isSubmitting = false;
            this.importProgress.isLoading = false;
            if (!this.completedHandledForCurrentImport) {
              this.completedHandledForCurrentImport = true;
              this.showSucceedSnackbar('Revit проект успешно загружен');
              const added = this.lastMismatchWarning?.only_in_structure;
              if (added?.length) {
                showInfoToast('Добавлены этажи: ' + added.join(', '));
              }
              this.$emit('success');
            }
            this.lastMismatchWarning = null;
          } else if (status === 'error') {
            this.stopPolling();
            this.isSubmitting = false;
            this.importProgress.isLoading = false;
            this.importProgress.errorMessage = errorMessage || 'Ошибка при импорте';
            this.showErrorSnackbar(errorMessage || 'Ошибка при импорте');
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 1000);
    },
    
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    clearImportError() {
      this.importProgress.active = false;
      this.importProgress.errorMessage = null;
      this.importProgress.messages = [];
    }
  },
  beforeUnmount() {
    // Останавливаем polling при размонтировании
    this.stopPolling();
  },
  mounted() {
    this.loadData();
    if (this.initialOperationKey && this.importInProgress) {
      this.importProgress.active = true;
      this.importProgress.isLoading = true;
      this.importProgress.messages = [];
      this.startPolling(this.initialOperationKey);
    }
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar, showWarningSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, showWarningSnackbar };
  }
};
</script>

<style scoped lang="scss">
.project-load-revit {
  .form-section {
    margin-bottom: 24px;
  }
  
  .form-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
    color: #262626;
  }
  
  .bim-project-item {
    &:hover {
      background: #f5f5f5;
    }
    
    &.selected {
      background: #e3f2fd;
      border-left: 3px solid #1976d2;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
}
</style>

